/**
 * IndexedDB wrapper for chat history storage.
 *
 * Provides a simple async API for storing encrypted chat data in IndexedDB.
 * This isolates our storage from other pages on the domain and allows us
 * to enforce our own size limits without affecting other applications.
 *
 * Security: Uses two object stores:
 * - 'chat-history': Stores encrypted chat data (Uint8Array blobs)
 * - 'chat-keys': Stores non-extractable CryptoKey objects per user
 */

const DB_NAME = 'marriott-ai-chat';
const STORE_NAME = 'chat-history';
const KEY_STORE_NAME = 'chat-keys';
const DB_VERSION = 2;

export const MAX_STORAGE_BYTES = 5 * 1024 * 1024; // 5MB
export const MAX_CHAT_COUNT = 10;

// Cache the database connection
let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Open (or create) the IndexedDB database.
 * Connection is cached for reuse.
 */
function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = event => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
      // Create chat-keys store for non-extractable CryptoKeys (v2)
      if (!db.objectStoreNames.contains(KEY_STORE_NAME)) {
        db.createObjectStore(KEY_STORE_NAME);
      }
    };

    request.onsuccess = () => {
      const db = request.result;

      // Handle database version changes from other tabs (F19)
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };

      // Handle unexpected database closure (F19)
      db.onclose = () => {
        dbPromise = null;
      };

      resolve(db);
    };
    request.onerror = () => {
      dbPromise = null; // Reset cache on error
      reject(request.error);
    };
  });

  return dbPromise;
}

/**
 * Get a value from IndexedDB by key.
 * Returns null if the key doesn't exist.
 */
export async function idbGet(key: string): Promise<Uint8Array | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[IndexedDB] Error getting value:', error);
    return null;
  }
}

/**
 * Set a value in IndexedDB.
 * Throws an error if the operation fails.
 */
export async function idbSet(key: string, value: Uint8Array): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(value, key);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a value from IndexedDB by key.
 */
export async function idbDelete(key: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[IndexedDB] Error deleting value:', error);
  }
}

/**
 * Get the total size of all data in our IndexedDB store.
 * Returns size in bytes.
 */
async function idbGetSize(): Promise<number> {
  try {
    const db = await openDB();
    return await new Promise<number>(resolve => {
      try {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.openCursor();

        let totalSize = 0;

        request.onsuccess = event => {
          try {
            const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
            if (cursor) {
              const keySize = new Blob([String(cursor.key)]).size;
              const value = cursor.value;
              const valueSize = value instanceof Uint8Array ? value.byteLength : new Blob([String(value)]).size;
              totalSize += keySize + valueSize;
              cursor.continue();
            } else {
              resolve(totalSize);
            }
          } catch {
            resolve(0);
          }
        };

        request.onerror = () => {
          console.error('[IndexedDB] Cursor error while calculating size:', request.error);
          resolve(0);
        };

        tx.onerror = () => {
          console.error('[IndexedDB] Transaction error while calculating size:', tx.error);
          resolve(0);
        };
      } catch (error) {
        console.error('[IndexedDB] Error setting up size calculation:', error);
        resolve(0);
      }
    });
  } catch (error) {
    console.error('[IndexedDB] Error calculating size:', error);
    return 0;
  }
}

/**
 * Get storage usage information.
 */
export async function getIndexedDBUsage(): Promise<{
  used: number;
  total: number;
  percentage: number;
}> {
  let used = 0;
  try {
    used = await idbGetSize();
  } catch (error) {
    console.error('[IndexedDB] Error getting storage usage:', error);
  }
  return {
    used,
    total: MAX_STORAGE_BYTES,
    percentage: (used / MAX_STORAGE_BYTES) * 100,
  };
}

/**
 * Get a CryptoKey from the key store.
 * Returns null if the key doesn't exist.
 */
export async function idbGetKey(keyId: string): Promise<CryptoKey | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(KEY_STORE_NAME, 'readonly');
      const store = tx.objectStore(KEY_STORE_NAME);
      const request = store.get(keyId);

      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[IndexedDB] Error getting key:', error);
    return null;
  }
}

/**
 * Store a CryptoKey in the key store.
 */
export async function idbSetKey(keyId: string, key: CryptoKey): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE_NAME, 'readwrite');
    const store = tx.objectStore(KEY_STORE_NAME);
    const request = store.put(key, keyId);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a CryptoKey from the key store.
 */
export async function idbDeleteKey(keyId: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(KEY_STORE_NAME, 'readwrite');
      const store = tx.objectStore(KEY_STORE_NAME);
      const request = store.delete(keyId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[IndexedDB] Error deleting key:', error);
  }
}
